import {
  Firestore,
  Timestamp,
  collection,
  deleteField,
  doc,
  getDocs,
  limit,
  query,
  updateDoc,
  runTransaction,
  orderBy,
  where,
} from "firebase/firestore";
import { Customer } from "types";
import { cloudFunction } from "../helpers/cloudfunction";

/***
 * Add a bidder number to a customer.
 *
 * If no bidder number is provided, allocates the next sequential 4-digit number
 * starting from 1. Uses transactions to prevent race conditions.
 */
export async function setBidderNumber(
  firestore: Firestore,
  currentUid: string | null,
  marketId: string | null,
  customerId: string | null,
  params?: {
    // if not supplied one will be auto generated
    bidderNumber?: number | null;

    // the number generated is sticky to try and give customers the same number when they return
    // If you want to force a new number increment this
    attempt?: number;
  }
) {
  if (!marketId) throw new Error(`No market id`);
  if (!customerId) throw new Error(`No customer id`);

  async function write(bidderNumber: number | null) {
    let customerRef = doc(
      firestore,
      `markets/${marketId}/customers/${customerId}`
    );
    let update: Partial<Customer> = {
      bidderNumber:
        bidderNumber === null ? (deleteField() as any) : bidderNumber,
      updatedAt: Timestamp.now(),
      updatedBy: currentUid,
    };
    await updateDoc(customerRef, update);
    return bidderNumber;
  }

  let bidderNumber = params?.bidderNumber;

  if (bidderNumber === null) {
    // If the bidder number is explicitly set to null, remove it
    return await write(null);
  }

  if (bidderNumber) {
    let isUnique = await checkBidderNumberIsUnique(
      firestore,
      marketId,
      bidderNumber
    );
    if (!isUnique) {
      throw new Error(`Bidder number ${bidderNumber} is not unique`);
    }
  } else {
    bidderNumber = await allocateSequentialBidderNumber(
      firestore,
      marketId,
      customerId
    );
  }

  return await write(bidderNumber!);
}

/***
 * Allocate the next sequential bidder number using a transaction to prevent race conditions.
 *
 * Uses a transaction-based approach:
 * 1. Find the customer with the highest bidder number
 * 2. Assign the new customer the next number (highest + 1, starting from 1)
 * 3. Update the previous highest customer's updatedAt to create conflict detection
 * 4. If transaction fails due to conflict, Firestore will retry automatically
 */
async function allocateSequentialBidderNumber(
  firestore: Firestore,
  marketId: string,
  customerId: string
): Promise<number> {
  return await runTransaction(firestore, async (transaction) => {
    // Query for the customer with the highest bidder number
    let customersCol = collection(firestore, `markets/${marketId}/customers`);
    let highestQuery = query(
      customersCol,
      where("bidderNumber", "!=", null),
      orderBy("bidderNumber", "desc"),
      limit(1)
    );

    let highestSnapshot = await getDocs(highestQuery);

    let nextBidderNumber = 1; // Start at 1 if no customers exist
    let previousHighestCustomerRef = null;

    if (!highestSnapshot.empty) {
      let highestCustomer = highestSnapshot.docs[0];
      let currentHighest = highestCustomer.data().bidderNumber;
      nextBidderNumber = currentHighest + 1;
      previousHighestCustomerRef = highestCustomer.ref;
    }

    // Update the new customer with the next bidder number
    let newCustomerRef = doc(
      firestore,
      `markets/${marketId}/customers/${customerId}`
    );
    transaction.update(newCustomerRef, {
      bidderNumber: nextBidderNumber,
      updatedAt: Timestamp.now(),
    });

    // Update the previous highest customer to create conflict detection
    // This will cause the transaction to fail if another allocation happens simultaneously
    if (previousHighestCustomerRef) {
      transaction.update(previousHighestCustomerRef, {
        updatedAt: Timestamp.now(),
      });
    }

    return nextBidderNumber;
  });
}

export async function checkBidderNumberIsUnique(
  firestore: Firestore,
  marketId: string | null,
  bidderNumber: number
) {
  if (!marketId) throw new Error(`No market id`);

  let col = collection(firestore, `markets/${marketId}/customers`);
  let q = query(col, where("bidderNumber", "==", bidderNumber), limit(1));
  let snaps = await getDocs(q);
  return snaps.empty;
}

export async function clearAllBidderNumbers(marketId: string | null) {
  if (!marketId) throw new Error(`No market id`);

  let func = cloudFunction<
    {
      marketId: string;
    },
    {
      updated: number;
    }
  >("clearAllBidderNumbers");

  return func({ marketId });
}
